
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import StoreCard from './StoreCard';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Skeleton } from '@/components/ui/skeleton';
import { Container } from '@/components/ui/container';
import { Store, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import type { Profile, StoreApprovalStatus } from '@/context/AuthContext';

const FeaturedStores: React.FC = () => {
  const [stores, setStores] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStores = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: supabaseError } = await supabase
          .from('profiles')
          .select('*')
          .eq('is_store', true)
          .eq('store_approval_status', 'approved')
          .eq('is_blocked', false)
          .order('created_at', { ascending: false }) 
          .limit(10);

        if (supabaseError) {
          throw supabaseError;
        }
        
        // Data from Supabase should conform to the Profile type, including required created_at
        const typedStores = (data || []).map(store => ({
          ...store,
          id: store.id, // ensure id
          created_at: store.created_at, // ensure created_at is string
          role: store.role === 'admin' ? 'admin' : 'user',
          store_approval_status: store.store_approval_status as StoreApprovalStatus ?? null,
        })) as Profile[];
        setStores(typedStores);

      } catch (err: any) {
        console.error('Error fetching stores:', err);
        setError('Pati një problem gjatë ngarkimit të dyqaneve.');
      } finally {
        setLoading(false);
      }
    };

    fetchStores();
  }, []);

  const SectionHeader = () => (
    <div className="flex flex-col md:flex-row items-center justify-between mb-8">
      <div className="flex items-center">
        <Store size={32} className="mr-3 text-Primary-Purple" />
        <h2 className="text-3xl font-bold text-Charcoal-Gray dark:text-Pure-White">
          Dyqanet e Promovuara
        </h2>
      </div>
      <Link to="/stores">
        <Button variant="ghost" className="mt-4 md:mt-0 text-Primary-Purple hover:text-Primary-Purple/80 hover:bg-transparent">
          Shiko të gjitha
          <ArrowRight size={16} className="ml-2" />
        </Button>
      </Link>
    </div>
  );
  
  if (loading) {
    return (
      <Container className="py-12">
        <div className="flex flex-col md:flex-row items-center justify-between mb-8">
          <div className="flex items-center">
            <Store size={32} className="mr-3 text-Primary-Purple" />
            <h2 className="text-3xl font-bold text-Charcoal-Gray dark:text-Pure-White">
              Dyqanet e Promovuara
            </h2>
          </div>
          <Skeleton className="h-10 w-36 mt-4 md:mt-0" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
             <div key={i} className="rounded-xl bg-white dark:bg-Charcoal-Gray p-4 shadow-md">
              <Skeleton className="h-32 w-full rounded-lg mb-4" />
              <div className="flex items-center space-x-3 mb-4">
                <Skeleton className="h-16 w-16 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-12 text-center">
        <SectionHeader />
        <p className="text-red-500">{error}</p>
      </Container>
    );
  }

  if (stores.length === 0) {
    return null; 
  }

  return (
    <Container className="py-12">
      <SectionHeader />
      <Carousel
        opts={{
          align: "start",
          loop: stores.length > 2, 
        }}
        className="w-full"
      >
        <CarouselContent className="-ml-4">
          {stores.map((store) => (
            <CarouselItem key={store.id} className="pl-4 basis-full md:basis-1/2 lg:basis-1/3">
              <div className="p-1 h-full">
                <StoreCard store={store} />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {stores.length > (typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 3) && ( 
          <>
            <CarouselPrevious className="absolute left-[-20px] md:left-[-25px] top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white dark:bg-Charcoal-Gray/80 dark:hover:bg-Charcoal-Gray text-Primary-Purple dark:text-Light-Purple shadow-md transition-all" />
            <CarouselNext className="absolute right-[-20px] md:right-[-25px] top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white dark:bg-Charcoal-Gray/80 dark:hover:bg-Charcoal-Gray text-Primary-Purple dark:text-Light-Purple shadow-md transition-all" />
          </>
        )}
      </Carousel>
    </Container>
  );
};

export default FeaturedStores;
